// Purchase presets (A4). These recreate the current hard-coded purchase prints in
// backend helper/GRN/GRN_1.ts so they can be edited in the builder:
//   purchaseOrderA4   <- PurchaseOrder(resultArray, store, tax)
//   grnA4             <- GRN_2(resultArray, store, tax, "GRN")
//   purchaseInvoiceA4 <- GRN_2(resultArray, store, tax, "POI")  (poit_* fields)
import { text, field, hline, vline, box, col, table, fixedBand, tableBand, a4, preset } from "./helpers";

const W = 194; // A4 content width with 8 mm margins

// "a, b, c" of the non-blank DL numbers (the TS filters blanks and joins with ", ").
const dlJoin = (keys) => `MID(${keys.map((k) => `IF(${k}, ", " & ${k}, "")`).join(" & ")}, 3)`;
// Address parts joined with spaces, skipping blanks.
const addrJoin = (base, keys) => `TRIM(${keys.map((k) => `IF(${base}.${k}, ${base}.${k} & " ", "")`).join(" & ")})`;
// "\n**Label:**value" only when the value is present.
const optLine = (label, expr) => `{{ IF(${expr}, "\n**${label}:**" & ${expr}, "") }}`;

/* ------------------------------------------------------------------ *
 * Purchase Order
 * ------------------------------------------------------------------ */
const PO = {
  ink: "#0f172a",
  muted: "#475569",
  line: "#e5e7eb",
  titleBg: "#0b3a63",
  theadBg: "#1e293b",
  theadFg: "#f8fafc",
  gstHead: "#eef2ff",
  totalLbl: "#fff7ed",
  totalNumBg: "#f0fdf4",
  totalNum: "#064e3b",
  accent: "#0ea5e9",
  headingBg: "#f1f5f9",
};
const POL = { color: PO.line };

// Party block for pot_pay_to_address / pot_ship_to_address (nested JSON objects).
function poParty(addr, p) {
  const base = `order.${addr}`;
  return (
    `**{{ COALESCE(${base}.${p}_address_name, "-") }}**\n` +
    `{{ ${addrJoin(base, ["street", "town", "district", "city", "state", "country", "pincode"].map((k) => `${p}_address_${k}`))} }}` +
    optLine("GST", `${base}.${p}_gst`) +
    optLine("PAN", `${base}.${p}_pan`) +
    optLine("FSSAI NO", `${base}.${p}_fssai_no`) +
    `{{ IF(var.${p}_dl, "\n**DL NO:**" & var.${p}_dl, "") }}`
  );
}

export function purchaseOrderA4() {
  const sideHead = (x, y, w, h, label) => text(x, y, w, h, label, { bold: true, color: PO.ink, bg: PO.headingBg, valign: "middle", padding: 1 });
  const body = (x, y, w, h, content) => text(x, y, w, h, content, { fontSize: 7.5, padding: 1 });
  const lbl = { bold: true, color: PO.ink, bg: PO.totalLbl, valign: "middle", padding: 0.8 };
  const num = { bold: true, color: PO.totalNum, bg: PO.totalNumBg, valign: "middle", align: "right", padding: 0.8 };
  const totals = [
    ["GrossTotal:", "pot_sub_total", "fixed:2 | default:-"],
    ["TaxAmount:", "pot_total_gst", "fixed:2 | default:-"],
    ["Discount:", "pot_total_discount", "fixed:2"],
    ["Round Off value:", "pot_round_off_value", "fixed:2"],
    ["Other Charge:", "0", ""],
    ["TotalAmount:", "pot_total_amount", "fixed:2 | default:-"],
  ];
  const RH = 5; // totals row height
  const LX = 139; // left (GST) area = 10 of 14 columns
  const VX = 173; // value column

  return preset(
    "purchase_order",
    "Purchase Order (A4) - current format",
    a4(8),
    [
      fixedBand(
        "Header",
        63,
        [
          text(0, 0, W, 8, "Purchase Order", { bold: true, fontSize: 13.5, color: "#ffffff", bg: PO.titleBg, align: "center", valign: "middle" }),
          // From / To
          sideHead(0, 8, 24, 28, "From"),
          body(
            24,
            8,
            73,
            28,
            "**{{ store.company | default:- }}**\n{{ store.company_address | default:- }}" +
              optLine("GST", "store.company_gstn") +
              optLine("PAN", "store.pan_number") +
              optLine("FSSAI NO", "store.fssai_no") +
              '{{ IF(var.store_dl, "\n**DL NO:**" & var.store_dl, "") }}'
          ),
          sideHead(97, 8, 24, 28, "To"),
          body(121, 8, 73, 28, poParty("pot_pay_to_address", "cmr_pay")),
          // Order details / Shipping
          sideHead(0, 36, 24, 27, "Order Details"),
          body(
            24,
            36,
            73,
            27,
            "**PO No:** {{ inv_no | default:- }}\n" +
              "**PO Date:** {{ pot_document_date | date:'DD/MM/YYYY' | default:- }}\n" +
              "**Expected Date:** {{ pot_expected_delivery_date | date:'DD/MM/YYYY' | default:- }}\n" +
              "**Created By:** {{ created_by | default:- }}    **Updated By:** {{ order.updated_by | default:- }}"
          ),
          sideHead(97, 36, 24, 27, "Shipping"),
          body(121, 36, 73, 27, poParty("pot_ship_to_address", "cmr_ship")),
          // grid
          box(0, 8, W, 55, { borderColor: PO.line, borders: "trl" }),
          hline(0, 36, W, POL),
          vline(24, 8, 55, POL),
          vline(97, 8, 55, POL),
          vline(121, 8, 55, POL),
        ],
        { repeat: "top" }
      ),
      tableBand(
        "Items",
        table(
          "items",
          "items",
          0,
          W,
          [
            col("sno", "#", "SNO()", 4),
            col("code", "ProductCode", "item_code", 18, { format: "dash" }),
            col("hsn", "HsnCode", 'IF(item_hsn = "null", "-", item_hsn)', 15, { format: "dash" }),
            col("name", "ProductName", 'item_name & IF(item.item_baby_box_packing, "\n" & item.item_baby_box_packing, "")', 30, { format: "dash", style: { bold: true } }),
            col("generic", "Generic Name", "item_generic_name", 22, { format: "dash" }),
            col("mfr", "Manufacturer Name", "item_manufacturer_name", 24, { format: "dash" }),
            col("uom", "UomName", "item_uom", 11, { format: "dash" }),
            col("qty", "Qty", "COALESCE(item_quantity, 0)", 7),
            col("rate", "Purchase Rate", 'IF(item_unit_price, item_unit_price, "0.00")', 13),
            col("mrp", "MRP", 'IF(mrp, mrp, "0.00")', 12),
            col("disc", "Disc%", 'IF(item_discount_percentage, item_discount_percentage, "0.00")', 9),
            col("tax", "Tax%", 'IF(item_total_tax_percentage, item_total_tax_percentage, "0.00")', 9),
            col("amount", "Item total", 'IF(item_total_amount, item_total_amount, "0.00")', 17),
          ],
          {
            style: { fontSize: 7, grid: "all", borderWidth: 0.75, borderColor: PO.line, cellPadding: 0.8, color: PO.ink, zebra: "#f8fafc" },
            headerStyle: { bold: true, bg: PO.theadBg, color: PO.theadFg },
          }
        )
      ),
      fixedBand("Totals", totals.length * RH, [
        table(
          "gst",
          "tax",
          1,
          69,
          [
            col("gst", "GST%", "item_gst", 1, { format: "dash" }),
            col("taxable", "Taxable", "taxable", 1, { format: "fixed:2 | default:-" }),
            col("sgst", "SGST", "sgst", 1, { format: "fixed:2 | dash" }),
            col("cgst", "CGST", "gst", 1, { format: "fixed:2 | dash" }),
            col("igst", "IGST", "COALESCE(igst, 0)", 1, { format: "fixed:2" }),
          ],
          {
            y: 1,
            h: totals.length * RH - 2,
            style: { fontSize: 7, grid: "all", borderWidth: 0.75, borderColor: PO.line, cellPadding: 0.6, color: PO.muted, borders: "none" },
            headerStyle: { bold: true, bg: PO.gstHead, color: PO.ink },
          }
        ),
        totals.flatMap(([label, expr, fmt], i) => [
          text(LX, i * RH, VX - LX, RH, label, lbl),
          field(VX, i * RH, W - VX, RH, expr, fmt, num),
        ]),
        box(0, 0, W, totals.length * RH, { borderColor: PO.line, borders: "rbl" }),
        vline(LX, 0, totals.length * RH, POL),
        vline(VX, 0, totals.length * RH, POL),
        totals.slice(1).map((_, i) => hline(LX, (i + 1) * RH, W - LX, POL)),
      ]),
      fixedBand("Footer", 38, [
        text(0, 0, 24, 6, "Remarks", { bold: true, color: PO.ink, bg: PO.headingBg, valign: "middle", padding: 1 }),
        text(24, 0, W - 24, 6, "{{ remarks | default:- }}", { valign: "middle", padding: 1 }),
        text(0, 32, W, 6, "Authorised Signature", { bold: true, color: PO.accent, bg: PO.headingBg, align: "right", valign: "middle", padding: 1 }),
        box(0, 0, W, 38, { borderColor: PO.line, borders: "rbl" }),
        vline(24, 0, 6, POL),
        hline(0, 6, W, POL),
        hline(0, 32, W, POL),
      ]),
    ],
    {
      defaults: { fontFamily: "Courier New", fontSize: 8, color: PO.muted },
      variables: [
        { name: "store_dl", expr: dlJoin(["store.dl_no_20_20b", "store.dl_no_21_21b", "store.dl_no_21c", "store.dl_no_21f"]) },
        { name: "cmr_pay_dl", expr: dlJoin(["20_20b", "21_21b", "21c", "21f"].map((k) => `order.pot_pay_to_address.cmr_pay_dl_no_${k}`)) },
        { name: "cmr_ship_dl", expr: dlJoin(["20_20b", "21_21b", "21c", "21f"].map((k) => `order.pot_ship_to_address.cmr_ship_dl_no_${k}`)) },
      ],
    }
  );
}

/* ------------------------------------------------------------------ *
 * GRN_2: Goods Receipt Note ("GRN") and Purchase Invoice ("POI")
 * ------------------------------------------------------------------ */
function grnLike(isGRN) {
  const p = isGRN ? "gort" : "poit";
  const cell = { padding: 0.8 };
  const RH = 5;
  const LX = 140; // left (GST) area
  const VX = 170; // value column
  const totals = [
    ["GrossTotal:", `${p}_sub_total`, "fixed:2 | default:-"],
    ["TaxAmount:", `${p}_total_gst`, "fixed:2 | default:-"],
    ["Discount:", `COALESCE(${p}_total_discount, 0)`, "fixed:2"],
    ["Other Charge:", `COALESCE(order.${p}_other_charges, 0)`, "fixed:2"],
    ["Round Off:", `COALESCE(${p}_round_off_value, 0)`, "fixed:2"],
    ["TotalAmount:", `${p}_total_amount`, "fixed:2 | default:-"],
  ];

  return preset(
    isGRN ? "good_receipt_note" : "purchase_invoice",
    isGRN ? "Goods Receipt Note (GRN) (A4) - current format" : "Purchase Invoice (A4) - current format",
    a4(8),
    [
      fixedBand(
        "Header",
        30,
        [
          text(0, 0, W, 6, isGRN ? "Goods Recipient Note" : "Purchase Invoice", { bold: true, fontSize: 9, align: "center", valign: "middle" }),
          text(0, 6, 97, 24, "From,\n**{{ store.company | default:- }}**\n{{ store.company_address | default:- }}", { ...cell, valign: "middle" }),
          text(
            97,
            6,
            48.5,
            6,
            isGRN ? "GRN No: {{ inv_no | default:- }}" : "Bill No: {{ poit_custom_invoice_number | default:- }}",
            { ...cell, valign: "middle" }
          ),
          text(145.5, 6, 48.5, 6, `${isGRN ? "GRN Date:" : "Bill Date:"} {{ document_date | date:'DD/MM/YYYY' | default:- }}`, { ...cell, valign: "middle" }),
          text(
            97,
            12,
            97,
            18,
            `To,\n**{{ cmr_name | default:- }}\nStoreCode: {{ order.store_id | default:- }}**\nAddress: {{ ${p}_billing_address | default:- }}`,
            { ...cell, valign: "middle" }
          ),
          box(0, 0, W, 30, { borders: "trl" }),
          hline(0, 6, W),
          vline(97, 6, 24),
          hline(97, 12, 97),
          vline(145.5, 6, 6),
        ],
        { repeat: "top" }
      ),
      tableBand(
        "Items",
        table(
          "items",
          "batches",
          0,
          W,
          [
            col("sno", "#", "SNO()", 5),
            col("code", "ProductCode", "item_code", 18, { format: "dash" }),
            col("hsn", "HsnCode", 'IF(item_hsn = "null", "-", item_hsn)', 15.5, { format: "dash" }),
            col("name", "ProductName", "item_name", 28.5, { format: "dash" }),
            col("uom", "UomName", "item_uom", 12, { format: "dash" }),
            col("batch", "BatchNo", "item_batch_number", 13, { format: "dash" }),
            col("exp", "Exp", "item_exp_date", 17.5, { format: "date:YYYY-MM-DD | default:-" }),
            col("qty", "Qty", "IF(item_sellable_quantity, item_sellable_quantity, IF(item_batch_quantity, item_batch_quantity, 0))", 7),
            col("rate", "Rate", 'IF(item_batch_purchase_rate, item_batch_purchase_rate, "0.00")', 11),
            col("mrp", "MRP", 'IF(item_batch_unit_price, item_batch_unit_price, "0.00")', 11),
            col("disc", "DiscAmt", 'IF(item_batch_discount_amount, item_batch_discount_amount, "0.00")', 12.5),
            col("subtotal", "Subtotal", 'IF(item_batch_total_purchase_rate AND item_batch_tax_amount, item_batch_total_purchase_rate - item_batch_tax_amount, "")', 14, {
              format: "fixed:2 | default:-",
            }),
            col("tax", "TaxAmount", 'IF(item_batch_tax_amount, item_batch_tax_amount, "0.00")', 15),
            col("amount", "Total Amount", 'IF(item_batch_total_purchase_rate, item_batch_total_purchase_rate, "0.00")', 14),
          ],
          {
            style: { fontSize: 7, grid: "all", borderWidth: 0.75, borderColor: "#000000", cellPadding: 0.8 },
            headerStyle: { bold: true },
          }
        )
      ),
      fixedBand("Totals", totals.length * RH, [
        table(
          "gst",
          "tax",
          1,
          (LX - 2) / 2,
          [
            col("gst", "GST%", "item_gst", 1, { format: "dash" }),
            col("taxable", "Taxable", "taxable", 1, { format: "fixed:2 | default:-" }),
            col("sgst", "SGST", "COALESCE(sgst, 0)", 1, { format: "fixed:2" }),
            col("cgst", "CGST", "COALESCE(gst, 0)", 1, { format: "fixed:2" }),
            col("igst", "IGST", "COALESCE(igst, 0)", 1, { format: "fixed:2" }),
          ],
          {
            y: 1,
            h: totals.length * RH - 2,
            style: { fontSize: 7, grid: "all", borderWidth: 0.75, borderColor: "#000000", cellPadding: 0.6, borders: "none" },
            headerStyle: { bold: true },
          }
        ),
        totals.flatMap(([label, expr, fmt], i) => [
          text(LX, i * RH, VX - LX, RH, label, { ...cell, valign: "middle" }),
          field(VX, i * RH, W - VX, RH, expr, fmt, { ...cell, valign: "middle", align: "right" }),
        ]),
        box(0, 0, W, totals.length * RH, { borders: "rbl" }),
        vline(LX, 0, totals.length * RH),
        vline(VX, 0, totals.length * RH),
        totals.slice(1).map((_, i) => hline(LX, (i + 1) * RH, W - LX)),
      ]),
      fixedBand("Footer", 41, [
        text(0, 0, W, 6, "Remarks:{{ remarks | default:- }}", { ...cell, valign: "middle" }),
        text(0, 32, W, 9, "for, {{ cmr_name | default:- }}\nAuthorisedSignature", { ...cell, align: "right", valign: "middle" }),
        box(0, 0, W, 41, { borders: "rbl" }),
        hline(0, 6, W),
        hline(0, 32, W),
      ]),
    ],
    { defaults: { fontFamily: "Courier New", fontSize: 8, color: "#000000" } }
  );
}

export function grnA4() {
  return grnLike(true);
}

export function purchaseInvoiceA4() {
  return grnLike(false);
}
